import React, { useState, useEffect } from "react";
import { Input, Select, Button as ButtonAntd } from "antd";
import { Button, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function CategoryEditor({
  category,
  parentCategory,
  onSave,
  mode = "view",
  onCancel,
  submitting,
}) {
  const [parentNode, setParentNode] = useState("");
  const [form, setForm] = useState({
    name: "",
    description: "",
    type: category?.type || "ROOT",
  });

  useEffect(() => {
    if (mode === "create") {
      setParentNode(parentCategory?.name || "Root");
      setForm({ name: "", description: "", type: "" });
    } else if (mode === "createRoot") {
      setParentNode("");
      setForm({ name: "", description: "", type: "ROOT" });
    } else if (mode === "edit" || mode === "view") {
      setParentNode(""); // clear parentNode in view/edit
      setForm({
        name: category?.name || "",
        description: category?.description || "",
        type: category?.type || "ROOT",
      });
    }
  }, [category, parentCategory, mode]);

  if (mode === "default") {
    return (
      <Card className="custom-shadow">
        <Card.Body>
          <h6>Manage categories</h6>
          <p className="text-xs">
            Add, update, and organize task categories for better task
            management.
          </p>
          <Button
            variant="link"
            type="primary"
            size="sm"
            className="text-xs p-0 m-0 text-decoration-underline"
            onClick={() => onSave && onSave("createRoot")}
          >
            <FontAwesomeIcon icon="plus" /> New Root Category
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="mt-2 mt-lg-0">
      {mode === "view" && (
        <>
          <h6 className="text-sm fw-bold">Category Details</h6>
          <div>
            <label className="text-xxs">Name</label>
            <h6>{form.name}</h6>
          </div>
          <div>
            <label className="text-xxs">Description</label>
            <h6>{form.description}</h6>
          </div>
          <div>
            <label className="text-xxs">Type</label>
            <h6>{form.type}</h6>
          </div>
          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <Button
              variant="secondary"
              size="sm"
              className="text-xs"
              onClick={onCancel}
            >
              Close
            </Button>

            <Button
              variant="success"
              size="sm"
              className="text-xs"
              onClick={() => onSave && onSave("edit")}
            >
              Edit
            </Button>
          </div>
        </>
      )}

      {(mode === "edit" || mode === "create" || mode === "createRoot") && (
        <>
          <h6 className="text-sm fw-bold">
            {mode === "edit"
              ? `Edit Category: ${form.name}`
              : mode === "create"
              ? "New SubCategory"
              : "New Root Category"}
          </h6>

          {mode === "create" && (
            <div style={{ marginTop: 16 }}>
              <label className="text-xxs">Root Category</label>
              <Input
                value={parentNode}
                className="text-xs text-muted"
                disabled
              />
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <label className="text-xxs">Name</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="text-xs text-muted"
            />

            {(mode === "edit" || mode === "createRoot") && (
              <p className="text-xxs text-secondary">
                Category name is shown in the category tree, used for organizing
                related items, and referenced throughout the app.
              </p>
            )}
          </div>

          {mode === "create" && (
            <div style={{ marginTop: 16 }}>
              <label className="text-xxs">Category Type: </label>
              <Select
                style={{ width: 200 }}
                placeholder="Select node type"
                onChange={(value) => setForm({ ...form, type: value })}
                options={[
                  { value: "INTERMEDIATE", label: "Intermediate" },
                  { value: "LEAF", label: "Leaf" },
                ]}
                className="text-xs ms-lg-2"
              />
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <label className="text-xxs">Description</label>
            <Input.TextArea
              rows={mode === "edit" ? 3 : 2}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="text-xs text-muted"
            />
            {(mode === "edit" || mode === "createRoot") && (
              <p className="text-xxs text-secondary">
                This helps to explain the purpose of the category, clarify its
                contents, and may appear in various parts of the app.
              </p>
            )}
          </div>

          <div style={{ marginTop: 24, display: "flex", gap: 8 }}>
            <Button
              variant="secondary"
              className="text-xs"
              size="sm"
              onClick={onCancel}
            >
              Close
            </Button>

            <ButtonAntd
              type="primary"
              loading={submitting}
              iconPlacement="end"
              size="sm"
              className="bg-success"
              style={{ fontFamily: "Inter" }}
              onClick={() => onSave(form)}
            >
              {submitting ? "Saving" : "Save"}
            </ButtonAntd>
          </div>
        </>
      )}
    </div>
  );
}
